<script setup lang="ts">
import { computed, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { fetchPaginatedAssetsQueryOptions } from '@/features/assets/queries'
import DataTable from '@/components/data-table.vue'
import DataTableHeader from '@/components/data-table-header.vue'
import DataTableCell from '@/components/data-table-cell.vue'
import { AssetDeliveryStatus, assetTypeDisplayNames } from '@/features/assets/constants'
import { formatNumber } from '@/helpers/formatting'
import BadgeTeal from '@/components/badge-teal.vue'
import BadgeRed from '@/components/badge-red.vue'
import PageContent from '@/components/page-content.vue'
import PageHeader from '@/components/page-header.vue'
import ButtonPrimary from '@/components/button-primary.vue'
import { faHandPointUp, faMagnifyingGlass, faMoneyCheckPen, faPlus } from '@fortawesome/pro-regular-svg-icons'
import InputText from '@/components/input-text.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import DataTablePagination from '@/components/data-table-pagination.vue'
import { usePaginationQueryParameters } from '@/shared/composables/usePaginationQueryParameters'
import { fetchOwnersQueryOptions } from '@/shared/queries'
import InputSelect from '@/components/input-select.vue'
import InputLabel from '@/components/input-label.vue'
import { useQueryParameter } from '@/shared/composables/useQueryParameter'
import { z } from 'zod'
import AddManualAssetModal from '@/features/assets/components/AddManualAssetModal.vue'
import { useModalState } from '@/shared/composables/useModalState'
import ButtonSecondary from '@/components/button-secondary.vue'
import LinkButtonSecondary from '@/shared/components/LinkButtonSecondary.vue'
import BadgeSecondary from '@/components/badge-secondary.vue'
import DropdownContext from '@/components/dropdown-context.vue'
import DropdownItem from '@/components/dropdown-item.vue'
import { makeCoreUrl } from '@/helpers/routing'
import useLoader from '@/composables/loader'
import { useAcceptAsset } from '@/features/assets/mutations'
import useNotifications from '@/composables/notifications'
import { Log } from '@/shared/utils/logging/loggers'
import { useRouter } from 'vue-router'
import ContentLoader from '@/components/content-loader.vue'

const router = useRouter()
const { wrap } = useLoader()
const { addSuccess, addError } = useNotifications()
const { mutateAsync: acceptAssetAsync } = useAcceptAsset()
const { isOpen: isModalOpen, open: openModal, close: closeModal } = useModalState()
const { page, pageSize, filter, sorting, debounced } = usePaginationQueryParameters()
const selectedOwner = useQueryParameter('owner', { schema: z.number().int().gt(0) })
const { data, isError, error, isFetching } = useQuery(
  fetchPaginatedAssetsQueryOptions({
    page: debounced.page,
    pageSize: debounced.pageSize,
    filter: debounced.filter,
    sorting: debounced.sorting,
    owner: selectedOwner,
  })
)

// Reset the page when the owner changes
watch(selectedOwner, () => page.value = undefined)

const { data: owners } = useQuery(fetchOwnersQueryOptions())
const dropDownOwners = computed(
  () => {
    const items: { value: number|undefined, text: string }[] = [{ value: undefined, text: 'All' }]

    return items.concat(
      (owners.value ?? []).map(x => ({ value: x.id, text: x.name }))
    )
  }
)

function acceptAsset(assetCode: string) {
  wrap(
    () => acceptAssetAsync(assetCode)
  ).then(
    () => {
      addSuccess({ title: 'Asset accepted successfully.' })
      router.push({ name: 'assets.meters', params: { assetCode } })
    },
    err => {
      Log.error('Failed to accept asset', { assetCode, reason: err })
      addError({ title: 'Failed to accept asset.', message: 'Please try again. If the problem persists, please contact support.' })
    }
  )
}

</script>

<template>
  <AddManualAssetModal v-if="isModalOpen" @close="closeModal" />

  <PageHeader>
    <div class="d-flex">
      <ButtonPrimary :icon="faPlus" @click="openModal">New Asset</ButtonPrimary>
      <div class="d-flex align-items-center ms-4">
        <InputLabel>Owner</InputLabel>
        <InputSelect :items="dropDownOwners"
                     @update:selected="selectedOwner = $event"
                     :selected="selectedOwner"
                     class="ms-2"
                     item-value="value"
                     item-text="text" />

      </div>
    </div>
    <div>
      <InputText v-model:value="filter" placeholder="Search">
        <template #suffix><FontAwesomeIcon :icon="faMagnifyingGlass" /></template>
      </InputText>
    </div>
  </PageHeader>
  <PageContent>
    <ContentLoader v-if="isFetching" loading variant="table" />
    <div v-else-if="isError"><pre>{{ JSON.stringify(error, null, 2) }}</pre></div>
    <template v-else-if="data">
      <DataTable :records="data.items ?? []" v-model:sorting="sorting">
        <template #headers="{ items }">
          <DataTableHeader name="asset">Asset</DataTableHeader>
          <DataTableHeader name="project">Project</DataTableHeader>
          <DataTableHeader name="name">Name</DataTableHeader>
          <DataTableHeader name="owner">Owner</DataTableHeader>
          <DataTableHeader name="type">Product Type</DataTableHeader>
          <DataTableHeader name="size" class="text-end">Size (kWh)</DataTableHeader>
          <DataTableHeader :sort="false">Status</DataTableHeader>
          <DataTableHeader :sort="false">{{ items.some(x => x.status !== AssetDeliveryStatus.Operational) ? 'Action' : '' }}</DataTableHeader>
        </template>

        <template #record="{ record }">
          <DataTableCell>{{ record.assetCode }}</DataTableCell>
          <DataTableCell>{{ record.projectCode }}</DataTableCell>
          <DataTableCell>{{ record.name }}</DataTableCell>
          <DataTableCell>{{ record.owner }}</DataTableCell>
          <DataTableCell>{{ assetTypeDisplayNames[record.type] }}</DataTableCell>
          <DataTableCell class="text-end">{{ formatNumber(record.systemSize, '-') }}</DataTableCell>
          <DataTableCell>
            <BadgeTeal v-if="record.status === AssetDeliveryStatus.Operational">Operational</BadgeTeal>
            <BadgeRed v-else-if="record.status === AssetDeliveryStatus.SetupIncomplete">Setup Incomplete</BadgeRed>
            <BadgeSecondary v-else-if="record.status === AssetDeliveryStatus.PendingDelivery">Pending Delivery</BadgeSecondary>
          </DataTableCell>
          <DataTableCell>
            <LinkButtonSecondary v-if="record.status === AssetDeliveryStatus.SetupIncomplete"
                                 outline="teal"
                                 :icon="faMoneyCheckPen"
                                 :to="{ name: 'assets.meters', params: { assetCode: record.assetCode } }">
              Setup
            </LinkButtonSecondary>
            <ButtonSecondary v-else-if="record.status === AssetDeliveryStatus.PendingDelivery"
                             @click="acceptAsset(record.assetCode)"
                             :icon="faHandPointUp">
              Accept & Setup
            </ButtonSecondary>
            <DropdownContext v-else>
              <DropdownItem :href="{ name: 'assets.settings', params: { assetCode: record.assetCode } }">
                Settings
              </DropdownItem>
              <DropdownItem :href="makeCoreUrl(`ProjectLead/Edit?id=${encodeURIComponent(record.encryptedProjectCode)}`)">
                View Project
              </DropdownItem>
            </DropdownContext>
          </DataTableCell>
        </template>
      </DataTable>
      <DataTablePagination :total-records="data.meta.totalRecords"
                           :page="data.meta.page"
                           @update:page="page = $event"
                           :page-size="data.meta.pageSize"
                           @update:page-size="pageSize = $event" />
    </template>

  </PageContent>
</template>

<style scoped lang="scss">

</style>
